<template>
  <section id="lista-parcerias">
    <div class="parcerias-header">
      <FiltrosComponent
        :isCoordenador="isCoordenador"
        :nomeTela="'parceria'"
        :filtrosIniciais="filtrosSelecionados"
        @pesquisar="executarBusca"
        @aplicarFiltros="onAplicarFiltros"
      />

      <button
        v-if="isCoordenador"
        type="button"
        class="btn-cadastrar-parceria"
        @click="acessarFormulario"
      >
        Cadastrar parceria
      </button>
    </div>

    <div class="conteudo-parcerias">
      <div id="parcerias">
        <Carregamento v-if="carregando" />

        <template v-else>
          <div
            v-for="parceria in parceriasExibidas"
            :key="parceria.id"
            class="parceria"
            @click="abrirParceria(parceria.id)"
          >
            <div class="card">
              <div class="situacao-main">
                <h6
                  class="situacao"
                  :class="parceria.statusClasse"
                >
                  {{ parceria.status }}
                </h6>
              </div>
              <div class="card-body">
                <div class="card-head">
                  <h5>
                    {{ parceria.tipo }}
                  </h5>

                  <p>
                    <strong>Ação:</strong>
                    {{ parceria.nomeAcao }}
                  </p>

                  <p>
                    <strong>Coordenador:</strong>
                    {{ parceria.nomeCoordenador }}
                  </p>
                </div>

                <div class="infos">
                  <p>
                    <strong>Data da solicitação:</strong>
                    {{ formatarData(parceria.dataSolicitacao) }}
                  </p>
                </div>
              </div>
              <div class="botoes">
                <button
                  type="button"
                  class="btn-visualizar"
                  @click.stop="abrirParceria(parceria.id)"
                >
                  Visualizar
                </button>
              </div>
            </div>
          </div>

          <div
            v-if="parceriasExibidas.length === 0"
            class="nenhuma-parceria-container"
          >
            <h3 class="nenhuma-parceria">Nenhuma parceria encontrada</h3>
          </div>
        </template>
      </div>
    </div>

    <PaginacaoComponent
      v-if="!carregando && totalPaginas > 1"
      :total-paginas="totalPaginas"
      :pagina-atual="paginaAtual"
      @mudar-pagina="carregarPaginaParcerias"
    />

    <ModalAviso
      v-if="mostrarModalLogin"
      :sucesso="false"
      texto="Você precisa estar logado para efetuar o pedido de parceria"
      :mostrarBotoesLogin="true"
      @fecharModal="mostrarModalLogin = false"
      @login="irParaLogin"
    />
    <EscolhaFormulario
      v-if="mostrarModalFormularios"
      @fecharModal="mostrarModalFormularios = false"
      @formularioExterno="acessarFormularioExterno"
      @formularioInterno="acessarFormularioInterno"
    />
  </section>
</template>

<script setup>
import { ref, computed, watch } from "vue";

import { useRouter, useRoute } from "vue-router";

import { storeToRefs } from "pinia";

import { useAuthStore } from "@/stores/auth";

import FiltrosComponent from "@/components/common/FiltrosComponent.vue";
import PaginacaoComponent from "@/components/common/paginacaoComponent.vue";
import Carregamento from "@/components/common/Carregamento.vue";

import EscolhaFormulario from "@/components/modals/EscolhaFormulario.vue";
import ModalAviso from "@/components/common/aviso.vue";

import parceriaService from "@/service/parceriaService";

const props = defineProps({
  isAtiva: {
    type: Boolean,
    default: false,
  },

  isCoordenador: {
    type: Boolean,
    default: false,
  },

  acaoId: {
    type: String,
    default: null,
  },
});

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const { userScope, estaRenovando } = storeToRefs(authStore);
const mostrarModalLogin = ref(false);
const mostrarModalFormularios = ref(false);
const todasParcerias = ref([]);
const carregando = ref(false);
const paginaAtual = ref(0);
const totalPaginas = ref(1);
const temProximaPagina = ref(false);
const pesquisaParceria = ref("");
const filtrosSelecionados = ref({});

const parceriasExibidas = computed(() => {
  return todasParcerias.value;
});

const isLogado = computed(() => {
  const escopoAtual = estaRenovando.value
    ? authStore.user?.scope
    : userScope.value;

  return !!escopoAtual && escopoAtual !== "usuarioNaoLogado";
});

const podeAcessarFormularioInterno = computed(() => {
  return (
    authStore.hasRole("COORDENADOR") ||
    authStore.hasRole("COORDENADORIA") ||
    authStore.hasRole("PREX") ||
    authStore.hasRole("ADMINISTRADOR")
  );
});

const podeCadastrarParceria = computed(() => {
  return isLogado.value && podeAcessarFormularioInterno.value;
});

function executarBusca(termoRecebidoDoFilho) {
  pesquisaParceria.value =
    typeof termoRecebidoDoFilho === "string" ? termoRecebidoDoFilho.trim() : "";

  const query = {
    ...route.query,
    page: "0",
  };

  if (pesquisaParceria.value) {
    query.busca = pesquisaParceria.value;
  } else {
    delete query.busca;
  }

  router.replace({
    query,
  });
}

function onAplicarFiltros(dadosRecebidos) {
  filtrosSelecionados.value = {
    ...(dadosRecebidos || {}),
  };

  const query = {
    ...route.query,
    page: "0",
  };

  Object.keys(query).forEach((chave) => {
    if (chave !== "page" && chave !== "busca") {
      delete query[chave];
    }
  });

  Object.entries(filtrosSelecionados.value).forEach(([chave, valor]) => {
    if (
      valor === null ||
      valor === undefined ||
      valor === "" ||
      (Array.isArray(valor) && valor.length === 0)
    ) {
      return;
    }

    if (Array.isArray(valor)) {
      query[chave] = valor.join(",");
    } else {
      query[chave] = valor;
    }
  });

  router.replace({
    query,
  });
}

async function carregarPaginaParcerias(pagina = 0) {
  const paginaSolicitada = Number(pagina);

  const paginaValida =
    Number.isInteger(paginaSolicitada) && paginaSolicitada >= 0
      ? paginaSolicitada
      : 0;

  carregando.value = true;

  try {
    const resposta = await parceriaService.listarParcerias({
      page: paginaValida,
      size: 6,
      sort: "dataSolicitacao,desc",
    });

    const conteudo = Array.isArray(resposta?.content) ? resposta.content : [];

    todasParcerias.value = conteudo.map((parceria) => ({
      id: parceria.id,

      acaoExtensaoId: parceria.acaoExtensaoId,

      tituloAcao: parceria.tituloAcao || "Não informado",

      tipoParceria: parceria.tipoParceria,

      tipo: traduzirTipoParceria(parceria.tipoParceria),

      nomeAcao: parceria.nomeAcao || "Não informado",

      nomeCoordenador: parceria.nomeCoordenador || "Não informado",

      statusOriginal: parceria.status,

      status: traduzirStatus(parceria.status),

      statusClasse: obterClasseStatus(parceria.status),

      dataSolicitacao: parceria.dataSolicitacao,
    }));

    paginaAtual.value = Number.isInteger(resposta?.number)
      ? resposta.number
      : paginaValida;

    totalPaginas.value =
      Number.isInteger(resposta?.totalPages) && resposta.totalPages > 0
        ? resposta.totalPages
        : 1;

    temProximaPagina.value =
      resposta?.last === false && paginaAtual.value < totalPaginas.value - 1;
  } catch (erro) {
    console.error("Erro ao carregar parcerias:", erro);

    todasParcerias.value = [];

    paginaAtual.value = paginaValida;

    totalPaginas.value = 1;

    temProximaPagina.value = false;
  } finally {
    carregando.value = false;
  }
}
function traduzirStatus(status) {
  switch (status) {
    case "PENDENTE":
      return "ENVIADO";

    case "EM_ANALISE":
      return "EM ANÁLISE";

    case "AGUARDANDO_AJUSTES":
      return "AGUARDANDO AJUSTES";

    case "APROVADA":
      return "APROVADA";

    case "REPROVADA":
      return "REPROVADA";

    default:
      return status || "NÃO INFORMADO";
  }
}

function obterClasseStatus(status) {
  switch (status) {
    case "PENDENTE":
      return "enviado";

    case "EM_ANALISE":
      return "em-analise";

    case "AGUARDANDO_AJUSTES":
      return "aguardando-ajustes";

    case "APROVADA":
      return "aprovada";

    case "REPROVADA":
      return "reprovada";

    default:
      return "status-desconhecido";
  }
}

function traduzirTipoParceria(tipo) {
  switch (tipo) {
    case "EXTERNA":
      return "Parceria externa";

    case "INTRA_MUROS":
      return "Parceria interna";

    default:
      return tipo || "Tipo não informado";
  }
}

function formatarData(data) {
  if (!data) {
    return "Não informado";
  }

  const dataObj = new Date(data);

  if (Number.isNaN(dataObj.getTime())) {
    return "Data inválida";
  }

  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(dataObj);
}

watch(
  () => route.query,
  (newQuery) => {
    pesquisaParceria.value =
      typeof newQuery.busca === "string" ? newQuery.busca : "";

    const novosFiltros = {};

    Object.entries(newQuery).forEach(([chave, valor]) => {
      if (chave === "page" || chave === "busca") {
        return;
      }

      if (typeof valor === "string" && valor.includes(",")) {
        novosFiltros[chave] = valor.split(",");
      } else {
        novosFiltros[chave] = valor;
      }
    });

    filtrosSelecionados.value = novosFiltros;

    const paginaQuery = Number(newQuery.page);

    const pagina =
      Number.isInteger(paginaQuery) && paginaQuery >= 0 ? paginaQuery : 0;

    carregarPaginaParcerias(pagina);
  },
  {
    deep: true,
    immediate: true,
  }
);

function abrirParceria(id) {
  if (!id) {
    return;
  }

  router.push({
    name: "ParceriaView",

    params: {
      parceriaId: id,
    },
  });
}

function acessarFormulario() {
  if (!isLogado.value) {
    mostrarModalLogin.value = true;
    return;
  }

  if (!podeCadastrarParceria.value) {
    return;
  }

  mostrarModalFormularios.value = true;
}

function acessarFormularioExterno() {
  if (!podeCadastrarParceria.value) {
    mostrarModalFormularios.value = false;

    return;
  }

  mostrarModalFormularios.value = false;

  router.push({
    name: "formulario-parceria-externa",

    params: {
      id: route.params.id,
    },
  });
}

function acessarFormularioInterno() {
  if (!podeCadastrarParceria.value) {
    mostrarModalFormularios.value = false;

    return;
  }

  mostrarModalFormularios.value = false;

  router.push({
    name: "formulario-parceria-interna",

    params: {
      id: route.params.id,
    },
  });
}

function irParaLogin() {
  mostrarModalLogin.value = false;

  router.push("/login");
}
</script>

<style scoped>
#lista-parcerias {
  width: 100%;
}

.parcerias-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 20px;
}

.btn-cadastrar-parceria {
  height: 100%;
  padding: 8px 16px;

  border: none;
  border-radius: 5px;

  background-color: var(--roxo-escuro1);
  color: var(--branco);

  font-size: 0.85rem;
  font-weight: 600;

  cursor: pointer;

  transition:
    background-color 0.25s ease,
    transform 0.25s ease,
    box-shadow 0.25s ease;

  white-space: nowrap;
}

.btn-cadastrar-parceria:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

/* =========================================================
   CONTEÚDO
   ========================================================= */

.conteudo-parcerias {
  width: 100%;
  min-height: 50vh;
}

#parcerias {
  display: grid;

  grid-template-columns: repeat(2, minmax(0, 1fr));

  gap: 1rem;

  width: 100%;
}

/* =========================================================
   CARD DA PARCERIA
   ========================================================= */

.parceria {
  display: flex;

  width: 100%;
  min-width: 0;

  cursor: pointer;

  border-radius: 8px;

  background-color: transparent;

  transition:
    transform 0.25s ease,
    box-shadow 0.25s ease;
}

.parceria:hover {
  transform: translateY(-2px);

  box-shadow: 0 5px 14px rgba(0, 0, 0, 0.12);
}

/* =========================================================
   CARD INTERNO
   ========================================================= */

.card {
  position: relative;

  display: flex;
  flex-direction: row;
  align-items: stretch;

  width: 100%;
  min-width: 0;
  height: 235px;

  overflow: hidden;

  background-color: #ffffff;

  border: 1px solid rgba(0, 0, 0, 0.1);

  border-radius: 8px;

  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.08);

  transition:
    border-color 0.25s ease,
    box-shadow 0.25s ease;
}

.parceria:hover .card {
  border-color: rgba(0, 0, 0, 0.18);

  box-shadow: 0 5px 14px rgba(0, 0, 0, 0.1);
}

/* =========================================================
   STATUS LATERAL
   ========================================================= */

.situacao-main {
  display: flex;
  align-items: stretch;
  justify-content: center;

  width: 48px;
  min-width: 48px;
  height: 100%;

  flex-shrink: 0;
}

.situacao {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 100%;
  height: 100%;

  margin: 0;
  padding: 10px 4px;

  color: #ffffff;

  writing-mode: vertical-lr;
  transform: rotate(180deg);

  font-size: 0.85rem;
  font-weight: 600;

  line-height: 1;

  text-align: center;

  letter-spacing: 0.3px;
}

/* =========================================================
   CORES DOS STATUS
   ========================================================= */

.enviado {
  background-color: #f59e0b;
}

.em-analise {
  background-color: #2563eb;
}

.aguardando-ajustes {
  background-color: #d97706;
}

.aprovada {
  background-color: #2e7d32;
}

.reprovada {
  background-color: #d32f2f;
}

.status-desconhecido {
  background-color: #6b7280;
}
/* =========================================================
   CORPO DO CARD
   ========================================================= */

.card-body {
  display: flex;
  flex-direction: column;

  flex: 1;

  min-width: 0;

  gap: 12px;

  margin: 0;

  padding: 14px 12px 48px 12px;

  overflow: hidden;
}

/* =========================================================
   CABEÇALHO DO CARD
   ========================================================= */

.card-head {
  min-width: 0;
}

.card-head h5 {
  display: -webkit-box;

  margin: 0 0 8px;

  overflow: hidden;

  color: var(--preto);

  font-size: 1.05rem;
  font-weight: 600;

  line-height: 1.3;

  text-overflow: ellipsis;

  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.card-head p {
  margin: 0 0 5px;

  overflow: hidden;

  color: #444;

  font-size: 0.85rem;

  line-height: 1.35;

  text-overflow: ellipsis;

  white-space: nowrap;
}

.card-head strong,
.infos strong {
  color: #333;
}

/* =========================================================
   INFORMAÇÕES
   ========================================================= */

.infos {
  min-width: 0;

  padding-top: 2px;

  color: #555;

  font-size: 0.85rem;

  word-wrap: break-word;
}

.infos p {
  margin: 0 0 6px;

  overflow: hidden;

  line-height: 1.35;

  text-overflow: ellipsis;

  white-space: nowrap;
}

.botoes {
  position: absolute;
  right: 0;
  bottom: 0;
  z-index: 2;
}

.btn-visualizar {
  padding: 6px 20px;
  border: none;
  border-radius: 6px 0 0 0;
  background-color: var(--azul-escuro);
  color: var(--branco);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  outline: none;

  transition:
    background-color 0.2s ease,
    color 0.2s ease,
    transform 0.15s ease;
}

.btn-visualizar:hover {
  background-color: #5a3ea7;
  color: var(--branco);
}

.btn-visualizar:active {
  background-color: #49328a;
  color: var(--branco);
  transform: scale(0.97);
}

.btn-visualizar:focus {
  background-color: var(--azul-escuro);
  color: var(--branco);
  outline: none;
}

.btn-visualizar:focus-visible {
  background-color: var(--azul-escuro);
  color: var(--branco);
  outline: 2px solid var(--azul-escuro);
  outline-offset: 2px;
}

.nenhuma-parceria-container {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 100%;
  min-height: 180px;

  grid-column: 1 / -1;

  padding: 30px 20px;
}

.nenhuma-parceria {
  margin: 0;

  color: #666;

  font-size: 1rem;
  font-weight: 500;

  text-align: center;
}

/* =========================================================
   LOADING
   ========================================================= */

#parcerias > :deep(.carregamento),
#parcerias > .carregamento {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 100%;

  grid-column: 1 / -1;
}

/* =========================================================
   TABLET
   ========================================================= */

@media (max-width: 1200px) {
  #parcerias {
    grid-template-columns: 1fr;
  }
}

/* =========================================================
   MOBILE
   ========================================================= */

@media (max-width: 768px) {
  #parcerias {
    grid-template-columns: 1fr;
  }

  .parceria {
    width: 100%;
  }

  .card {
    height: 220px;
  }
}

/* =========================================================
   CELULAR
   ========================================================= */

@media (max-width: 576px) {
  .parcerias-header {
    flex-direction: column;
    align-items: stretch;
  }

  .btn-cadastrar-parceria {
    width: 100%;

    padding: 10px 15px;
  }

  #parcerias {
    grid-template-columns: 1fr;

    gap: 12px;
  }

  .card {
    height: auto;
    min-height: 235px;
  }

  .situacao-main {
    width: 42px;
    min-width: 42px;
  }

  .situacao {
    font-size: 0.75rem;
  }

  .card-body {
    padding: 12px 8px 48px 10px;
  }

  .card-head h5 {
    font-size: 0.95rem;
  }

  .card-head p,
  .infos p {
    white-space: normal;
  }

  .btn {
    padding: 6px 16px;

    font-size: 0.8rem;
  }
}
</style>
